import React, { useEffect, useState } from "react";
import PageContentFilterDropdown from "@shared/components/PageContentFilterDropdown";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import { ProgramCardProps } from "@shared/interfaces/ProgramFiles";
import ProgramCard from "@pages/Program/components/ProgramCard";
import { useNavigate } from "react-router-dom";
import { useProgram } from "@pages/Program/utils/useProgram";
import { toastService } from "@services/ToastService";
import SkeletonProgramCard from "@pages/Program/components/skeleton/SkeletonProgramCard";
import DeleteProgramModal from "@pages/Program/components/DeleteProgramModel";
import Pagination from "@shared/components/Pagination";
import NoPrograms from "./NoPrograms";
import { useSidebar } from "@services/SidebarContext";
import NewItemButton from "@shared/components/NewItemButton";
import { useUser } from "@services/UserContext";

const Programs: React.FC = () => {
  const navigate = useNavigate();

  const PROGRAMS_PER_PAGE = 8;

  const [dropdownFilter, setDropdownFilter] = useState<string>("Latest");
  const [programs, setPrograms] = useState<ProgramCardProps[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<ProgramCardProps>(
    {} as ProgramCardProps
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / PROGRAMS_PER_PAGE);

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  const { fetchPrograms, deleteProgram } = useProgram();
  const { refreshPrograms } = useSidebar();
  const { user } = useUser();

  const getPrograms = async () => {
    try {
      if (!user) return;

      setIsLoading(true);

      const offset =
        totalCount <= 8 ? 0 : (currentPage - 1) * PROGRAMS_PER_PAGE;

      const order = dropdownFilter.toLowerCase(); // latest | oldest

      const programsData = await fetchPrograms(false, {
        offset,
        limit: PROGRAMS_PER_PAGE,
        order,
      });

      setPrograms(programsData.programs);
      setTotalCount(programsData.total_count);
    } catch (error) {
      console.error("Failed to fetch programs:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 250);
    }
  };

  useEffect(() => {
    getPrograms();
  }, [dropdownFilter, dropdownFilter, currentPage]);

  const handleCreateProgram = () => {
    navigate(`/create-program`);
  };

  const handleProgramClick = (id: number) => {
    navigate(`/program/${id}`);
  };

  const handleEdit = (programId: number) => {
    navigate(`/program/${programId}/edit`);
  };

  const handleDeletePopup = (program: ProgramCardProps) => {
    setIsDeletePopupOpen(true);
    setSelectedProgram(program);
  };

  const handleDelete = async () => {
    try {
      await deleteProgram(selectedProgram.id);
      setPrograms((prev) => prev.filter((p) => p.id !== selectedProgram.id));
      setTotalCount((prevTotal) => {
        const newTotal = prevTotal - 1;

        const maxPagesAfterDelete = Math.ceil(newTotal / PROGRAMS_PER_PAGE);

        if (currentPage > maxPagesAfterDelete) {
          setCurrentPage(Math.max(1, currentPage - 1));
        }

        return newTotal;
      });
      getPrograms();
      refreshPrograms();
      setIsDeletePopupOpen(false);
    } catch (error) {
      console.error("Failed to delete program:", error);
      toastService.error("Failed to delete program");
    }
  };

  return (
    <div className="px-8 pb-[0.67rem] 2xl:pb-3 flex-1 flex flex-col">
      <SubTitleHeader
        title={`Programs`}
        subtitleStyle="text-gray-400 text-sm 2xl:text-base"
        spacer={true}
      >
        <PageContentFilterDropdown
          dropdownFilter={dropdownFilter}
          setDropdownFilter={setDropdownFilter}
        />
        <NewItemButton onClick={handleCreateProgram} title="Program" />
      </SubTitleHeader>

      {!isLoading && programs.length === 0 ? (
        <NoPrograms handleCreateProgram={handleCreateProgram} />
      ) : (
        <div className="pt-8 flex flex-col flex-1">
          <div className="flex-1">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {isLoading && (
                <>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <SkeletonProgramCard key={index} />
                  ))}
                </>
              )}

              {!isLoading &&
                programs.length > 0 &&
                programs.map((program, index) => (
                  <ProgramCard
                    key={index}
                    {...program}
                    handleDelete={() => handleDeletePopup(program)}
                    handleEdit={handleEdit}
                    onClick={() => handleProgramClick(program.id)}
                  />
                ))}
            </div>
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      <DeleteProgramModal
        isOpen={isDeletePopupOpen}
        selectedProgram={selectedProgram}
        onClose={() => setIsDeletePopupOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Programs;
